import type { RouteViewModel } from '../../../../types/routeViewModel';
import type { ExecutionDashboardSlice, ExecutionProgressHydration } from '../../../executionOperations/types';
import type { ExecutionIncident } from '../incidentFacade';
import type { ExecutionEventRow } from '../../../executionOperations/types';
import { enrichStops, mergeStopAddresses } from '../../../executionOperations/executionHelpers';
import { buildEffectiveExecutionView, getProjectionBlockers } from '../routeExecutionProjectionFacade';
import { availableOperationalActions } from '../executionActionsFacade';
import { buildCargoHumanizationIndex } from '../cargoHumanization';
import { buildOperationalTimelineFromHydration } from '../executionTimelineFacade';
import { buildStopContextMap, getActiveExecutionStop } from '../operationalContextFacade';
import { computeExecutionProgressView } from '../executionProgressFacade';
import { deriveRouteOperationalStatus } from '../routeOperationalStatus';
import type { ExecutionDashboardProjection, ExecutionProgressProjection } from './types';
import { buildRouteStatusProjection } from './routeStatusProjection';
import { buildActiveOperationProjection } from './activeOperationProjection';
import { buildVehicleCargoProjection } from './vehicleCargoProjection';
import { buildRouteFlowProjection } from './routeFlowProjection';
import {
  buildRouteStopPresentationContext,
  buildRouteStopPresentationIndex,
  type RouteStopPresentationContext,
  type RouteStopPresentationInput
} from '../../routeSummaryProjection';
import { buildIncidentsProjection } from './incidentProjection';
import { buildCompactTimelineProjection } from './compactTimelineProjection';
import { buildFinanceReadinessProjection } from './financeReadinessProjection';
import {
  buildCompletionReadinessProjection,
  type CompletionReadinessContext
} from './completionReadinessProjection';
import { buildCompletionLockProjection } from './completionLockProjection';
import type { OperationalCompletionSnapshotPayload } from '../../../operationalDomain/completion/completionSnapshot';

function mapProgress(
  hydration: ExecutionProgressHydration,
  dashboard: ExecutionDashboardSlice | null | undefined
): ExecutionProgressProjection | null {
  const view = computeExecutionProgressView({ hydration, dashboard });
  if (!view) return null;
  return {
    routePercent: view.routePercent,
    loadingPercent: view.loadingPercent,
    unloadingPercent: view.unloadingPercent,
    cargoPercent: view.cargoPercent,
    stopsCompleted: view.stopsCompleted,
    stopsTotal: view.stopsTotal,
    partialCargoCount: view.partialCargoCount,
    failedCargoCount: view.failedCargoCount,
    blockers: dashboard?.executionBlockers ?? hydration.progress.blockers ?? []
  };
}

export type BuildExecutionDashboardProjectionParams = {
  routeId: number;
  hydration: ExecutionProgressHydration;
  view: RouteViewModel;
  incidents?: ExecutionIncident[];
  events?: ExecutionEventRow[];
  dashboard?: ExecutionDashboardSlice | null;
  forceAdvanced?: boolean;
  completionContext?: CompletionReadinessContext;
  completionSnapshot?: OperationalCompletionSnapshotPayload | null;
};

export function buildExecutionDashboardProjection(
  params: BuildExecutionDashboardProjectionParams
): ExecutionDashboardProjection {
  const {
    routeId,
    hydration,
    view,
    incidents = [],
    dashboard = null,
    forceAdvanced = false,
    completionContext,
    completionSnapshot = null
  } = params;

  const effective = buildEffectiveExecutionView(hydration);
  const hydrationForProjection = effective
    ? { ...hydration, stops: effective.stops, cargo: effective.cargo, progress: effective.progress, financeReadiness: effective.financeReadiness }
    : hydration;

  const hydrationMerged = mergeStopAddresses(
    hydrationForProjection,
    (view.structure?.stops ?? []) as Array<Record<string, unknown>>
  );
  const enrichedStops = enrichStops(hydrationMerged, view.execution.tasks);
  const cargoIndex = buildCargoHumanizationIndex(view.cargo, view.applications);
  const stopContextMap = buildStopContextMap({
    hydration: hydrationMerged,
    enrichedStops,
    cargoIndex,
    view
  });
  const presentationCtx = buildRouteStopPresentationContext({
    applications: view.applications as RouteStopPresentationContext['applications'],
    loadingWarehouse: view.operation.loadingWarehouse as RouteStopPresentationContext['loadingWarehouse'],
    unloadingWarehouse: view.operation.unloadingWarehouse as RouteStopPresentationContext['unloadingWarehouse'],
    routeConsigneeName:
      (view.parties?.consignee as { name?: string | null } | undefined)?.name ?? null,
    routeLoadingCity: view.operation.direction?.loadingCity,
    routeUnloadingCity: view.operation.direction?.unloadingCity
  });
  const presentationByStopId = buildRouteStopPresentationIndex(
    (view.structure?.stops ?? []) as RouteStopPresentationInput[],
    presentationCtx
  );

  const activeStop = getActiveExecutionStop(hydrationForProjection, enrichedStops, stopContextMap);
  const activeOperation = buildActiveOperationProjection(activeStop);
  const routeStatus = buildRouteStatusProjection(hydrationForProjection, incidents);
  const progress = mapProgress(hydrationForProjection, dashboard);
  const vehicleCargo = buildVehicleCargoProjection({ hydration, cargoIndex });
  const routeFlow = buildRouteFlowProjection({
    enrichedStops,
    stopContextMap,
    activeExecutionStopUid: activeStop?.stop.executionStopUid ?? null,
    presentationByStopId
  });
  const incidentsProjection = buildIncidentsProjection(incidents);
  const rawTimeline = buildOperationalTimelineFromHydration(
    hydrationForProjection,
    incidents,
    stopContextMap
  );
  const timeline = buildCompactTimelineProjection(rawTimeline);
  const financeReadiness = buildFinanceReadinessProjection({
    readiness: hydrationForProjection.financeReadiness,
    hydration: hydrationForProjection,
    openDocumentTasks: view.execution.tasks.filter(
      (t) => t.status !== 'COMPLETED' && t.status !== 'DONE'
    ).length,
    incidents,
    documentRequirements: view.documents?.requirements ?? [],
    controlTasks: view.execution.tasks
  });

  const routeStatusForActions = deriveRouteOperationalStatus(hydrationForProjection, incidents, {
    operationallyFinalized: completionSnapshot != null
  });
  const completionLock = buildCompletionLockProjection({
    routeStatus: view.operation.status,
    snapshot: completionSnapshot
  });

  const actions = availableOperationalActions({
    hydration: hydrationForProjection,
    activeStop,
    routeStatus: routeStatusForActions,
    enrichedStops,
    forceAdvanced,
    completionLocked: completionLock.locked
  });

  const completionReadiness = buildCompletionReadinessProjection({
    actions,
    context: {
      ...completionContext,
      forceHideHints: completionLock.locked ? true : completionContext?.forceHideHints,
      executionBlockers: getProjectionBlockers(hydration)
    }
  });

  return {
    routeId,
    routeStatus,
    progress,
    activeOperation,
    vehicleCargo,
    routeFlow,
    incidents: incidentsProjection,
    financeReadiness,
    completionReadiness,
    completionLock,
    timeline,
    actions
  };
}
